import React, { useEffect, useState, useCallback } from 'react';
import AntToolbar from './AntToolbar';
import AntLocalization from '../utils/AntLocalization';
import AntWidgetSkeleton, { AntWidgetEmpty } from './AntWidgetSkeleton';
import useWidgetFirstLoad from './AntWidgetSkeleton/useWidgetFirstLoad';

export default function (props) {
    var [data, setData] = useState({});
    var [listData, setListData] = useState([]);
    var [loaded, markLoaded] = useWidgetFirstLoad(Boolean(props.url));

    var isListMode = Boolean(props.labelField);

    var load = useCallback(async () => {
        if (props.url) {
            props.API.post(props.url, { ...props.searchConditions }, (resp) => {
                if (resp && !resp.error && resp.data) {
                    if (isListMode) {
                        var rows = [];
                        if (resp.data instanceof Array) rows = resp.data;
                        else if (resp.data && resp.data.rows instanceof Array) rows = resp.data.rows;
                        else if (resp.data && resp.data.items instanceof Array) rows = resp.data.items;
                        setListData(rows);
                        if (props.onEmpty) props.onEmpty(rows.length === 0);
                    } else {
                        setData(resp.data);
                    }
                } else {
                    if (isListMode) {
                        setListData([]);
                        if (props.onEmpty) props.onEmpty(true);
                    } else setData({});
                }
                markLoaded();
            });
        }
    }, [props.API, props.url, props.searchConditions, isListMode, markLoaded]);

    useEffect(() => {
        load();
        var ms = ((props.refreshIntervalSec ?? 60) | 0) * 1000;
        if (ms <= 0) return;
        var interval = setInterval(load, ms);
        return () => clearInterval(interval);
    }, [load, props.refreshIntervalSec]);

    useEffect(() => {
        load();
    }, [props.searchConditions, props.url]);

    useEffect(() => {
        if (props.dataSource && !props.url) {
            if (isListMode) setListData(props.dataSource instanceof Array ? props.dataSource : []);
            else setData(props.dataSource);
        }
    }, [props.dataSource]);

    var formatValue = (val, dataType) => {
        if (val === undefined || val === null) return '\u2014';
        var num = parseFloat(val);
        if (isNaN(num)) return String(val);
        if (dataType === 'currency') return AntLocalization.translateCurrency(num);
        if (dataType === 'int') return AntLocalization.translateInteger(num);
        if (dataType === 'float') return AntLocalization.translateDecimal(num);
        if (dataType === 'percent') return (num * 100).toFixed(1) + '%';
        return String(val);
    };

    // ─── List mode: array data with labelField + valueField ───
    if (isListMode) {
        var labelField = props.labelField || 'name';
        var valueField = props.valueField || 'value';
        var valueDataType = props.valueDataType || 'currency';
        var maxItems = props.maxItems || 0;

        var displayData = maxItems > 0 ? listData.slice(0, maxItems) : listData;
        var padCount = maxItems > 0 && displayData.length > 0 ? Math.max(0, maxItems - displayData.length) : 0;

        if (!loaded) {
            return (
                <React.Fragment>
                    {props.title && (
                        <div className="ant-widget-info-toolbar">
                            <AntToolbar title={props.title} isH6={true} upperCase={true} />
                        </div>
                    )}
                    <AntWidgetSkeleton variant="infopanel" rows={maxItems > 0 ? maxItems : 5} showBars={props.showBars !== false} />
                </React.Fragment>
            );
        }

        var maxVal = 0;
        displayData.forEach((row) => {
            var v = Math.abs(parseFloat(row[valueField]) || 0);
            if (v > maxVal) maxVal = v;
        });

        return (
            <React.Fragment>
                {props.title && (
                    <div className="ant-widget-info-toolbar">
                        <AntToolbar title={props.title} isH6={true} upperCase={true} />
                    </div>
                )}
                <div className="ant-widget-info-panel">
                    <div className="ant-widget-info-panel__body">
                        {displayData.map((row, idx) => {
                            var label = row[labelField] || '';
                            if (typeof label === 'object') label = AntLocalization.translateText(label);
                            var value = row[valueField];
                            var numVal = Math.abs(parseFloat(value) || 0);
                            var ratio = maxVal > 0 ? numVal / maxVal : 0;

                            return (
                                <div key={idx} className="ant-widget-info-panel__row">
                                    <div className="ant-widget-info-panel__row-main">
                                        <span className="ant-widget-info-panel__dot" style={{ background: row.color || 'var(--AnteBaseColor1)' }} />
                                        <span className="ant-widget-info-panel__label">{label}</span>
                                        <span className="ant-widget-info-panel__value">{formatValue(value, valueDataType)}</span>
                                    </div>
                                    {props.showBars !== false && maxVal > 0 && (
                                        <div className="ant-widget-info-panel__bar">
                                            <div
                                                className="ant-widget-info-panel__bar-fill"
                                                style={{ width: ratio * 100 + '%', background: row.color || 'var(--AnteBaseColor1)' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {Array.from({ length: padCount }, (_, idx) => (
                            <div key={'pad-' + idx} className="ant-widget-info-panel__row" style={{ visibility: 'hidden' }}>
                                <div className="ant-widget-info-panel__row-main">
                                    <span className="ant-widget-info-panel__dot" />
                                    <span className="ant-widget-info-panel__label">{'\u00A0'}</span>
                                    <span className="ant-widget-info-panel__value">{'\u00A0'}</span>
                                </div>
                                {props.showBars !== false && <div className="ant-widget-info-panel__bar" />}
                            </div>
                        ))}
                        {displayData.length === 0 && <AntWidgetEmpty />}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    // ─── Static mode: flat object with predefined items ───
    var items = props.items || [];
    var footer = props.footer;

    var maxVal = 0;
    items.forEach((item) => {
        var v = parseFloat(data[item.dataField]) || 0;
        if (Math.abs(v) > maxVal) maxVal = Math.abs(v);
    });

    if (!loaded) {
        return (
            <React.Fragment>
                {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
                <AntWidgetSkeleton variant="infopanel" rows={items.length || 5} showBars={props.showBars !== false} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
            <div className="ant-widget-info-panel">
                <div className="ant-widget-info-panel__body">
                    {items.map((item, idx) => {
                        var value = data[item.dataField];
                        var numVal = Math.abs(parseFloat(value) || 0);
                        var ratio = maxVal > 0 ? numVal / maxVal : 0;
                        return (
                            <div key={idx} className="ant-widget-info-panel__row">
                                <div className="ant-widget-info-panel__row-main">
                                    {item.color && <span className="ant-widget-info-panel__dot" style={{ background: item.color }} />}
                                    <span className="ant-widget-info-panel__label">{AntLocalization.translateText(item.label)}</span>
                                    <span className="ant-widget-info-panel__value">{formatValue(value, item.dataType)}</span>
                                </div>
                                {props.showBars !== false && maxVal > 0 && (
                                    <div className="ant-widget-info-panel__bar">
                                        <div
                                            className="ant-widget-info-panel__bar-fill"
                                            style={{ width: ratio * 100 + '%', background: item.color || 'var(--AnteBaseColor1)' }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {footer && (
                    <div className="ant-widget-info-panel__footer">
                        <span className="ant-widget-info-panel__label">{AntLocalization.translateText(footer.label)}</span>
                        <span className="ant-widget-info-panel__value">{formatValue(data[footer.dataField], footer.dataType)}</span>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
